import { makeAutoObservable, runInAction } from 'mobx';
import { createFromIconfontCN } from '@ant-design/icons';
import { Team, User } from './common/model';
import { LoginApi } from './login/LoginApi';
import { ProfileApi } from './profile/ProfileApi';
import { GlobalApi } from './global/GlobalApi';
import type { EnumConfigOption, EnumConfigs, TeamDTO, UserInfoDTO } from './global/GlobalType';
import { enumListToRecord } from './global/enumConfigUtil';
import { serializeMap } from './utils/S';
import { Storage } from './utils/Storage';
import { needsProfileSetup } from './utils/profileSetup';
import { pickDefaultTeamId, recordTeamVisit, sortTeamListForUser } from './utils/teamVisitOrder';

export const IconFont = createFromIconfontCN({
  scriptUrl: '/iconfont/iconfont.js',
});

export class GlobalStore {
  isLoaded = false;
  /** init 完成前勿根据 isLoggedIn 跳转登录（避免刷新误判） */
  sessionChecked = false;
  isLoggedIn = false;
  enableLoad = false;
  currentUser: UserInfoDTO | null = null;
  bugFilterUserId = 0;
  currentTeamId = '';
  teamMap = new Map<string, Team>();
  userMap = new Map<string, User>();
  enumConfigs: EnumConfigs = {
    meetingOptions: [],
    bizLineOptions: [],
    taskTypeOptions: [],
    taskStatusOptions: [],
    userTypeOptions: [],
  };
  profileSetupPrompt = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get meetingRecord(): Record<string, EnumConfigOption> {
    return enumListToRecord(this.enumConfigs.meetingOptions);
  }

  get bizLineRecord(): Record<string, EnumConfigOption> {
    return enumListToRecord(this.enumConfigs.bizLineOptions);
  }

  get taskTypeRecord(): Record<string, EnumConfigOption> {
    return enumListToRecord(this.enumConfigs.taskTypeOptions);
  }

  get taskStatusRecord(): Record<string, EnumConfigOption> {
    return enumListToRecord(this.enumConfigs.taskStatusOptions);
  }

  get userTypeRecord(): Record<string, EnumConfigOption> {
    return enumListToRecord(this.enumConfigs.userTypeOptions);
  }

  get userInfo(): UserInfoDTO | null {
    return this.currentUser;
  }

  get teamId(): string {
    return this.currentTeamId;
  }

  get currentTeam(): Team | undefined {
    return this.teamMap.get(this.currentTeamId);
  }

  get isLogin(): boolean {
    return this.isLoggedIn;
  }

  async clearSession(): Promise<void> {
    this.isLoggedIn = false;
    this.enableLoad = false;
    this.currentUser = null;
    this.isLoaded = false;
    this.userMap.clear();
    await Storage.removeItem('token');
    await Storage.removeItem('teamId');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('teamId');
    localStorage.removeItem('teamMap');
    localStorage.removeItem('token');
  }

  loadFromLocalStorage(): void {
    const curUserString = localStorage.getItem('userInfo');
    if (!curUserString) {
      this.isLoggedIn = false;
      return;
    }
    this.isLoggedIn = true;
    this.currentUser = JSON.parse(curUserString) as UserInfoDTO;
    this.profileSetupPrompt = needsProfileSetup(this.currentUser);
    const mapRaw = localStorage.getItem('teamMap');
    if (mapRaw) {
      this.teamMap = new Map(JSON.parse(mapRaw) as [string, Team][]);
    }
    this.currentTeamId = localStorage.getItem('teamId') || '';
  }

  async init(): Promise<void> {
    this.loadFromLocalStorage();
    const href = window.location.hash || window.location.pathname;
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        token = await Storage.getItem('token');
      }

      if (token && this.currentUser) {
        runInAction(() => {
          this.enableLoad = true;
        });
        if (!this.currentUser.teamList?.length) {
          this.currentTeamId = '';
          localStorage.removeItem('teamId');
          localStorage.removeItem('teamMap');
          void Storage.removeItem('teamId');
          this.teamMap.clear();
          if (!href.includes('join') && !href.includes('login')) {
            window.location.hash = '#/join';
          }
          return;
        }
        this.syncCurrentTeamFromUser();
        if (this.currentUser?.id && this.currentTeamId) {
          recordTeamVisit(this.currentUser.id, this.currentTeamId);
        }
        await this.loadTeamData();
        return;
      }
      runInAction(() => {
        this.isLoggedIn = false;
      });
      if (!href.includes('login') && !href.includes('auth')) {
        window.location.hash = '#/login';
      }
    } finally {
      runInAction(() => {
        this.sessionChecked = true;
      });
    }
  }

  /** 有团队但未选 teamId 时（如 join 后进首页）补全本地团队上下文 */
  syncCurrentTeamFromUser(): void {
    const user = this.currentUser;
    if (!user?.id || !user.teamList?.length) {
      return;
    }
    const sortedTeams = sortTeamListForUser(user.teamList, user.id);
    runInAction(() => {
      user.teamList = sortedTeams;
      this.currentUser = user;
    });
    localStorage.setItem('userInfo', JSON.stringify(user));
    let currentTeamId = this.currentTeamId;
    if (!currentTeamId || !sortedTeams.some((t) => t.id === currentTeamId)) {
      currentTeamId = pickDefaultTeamId(sortedTeams, user.id);
      recordTeamVisit(user.id, currentTeamId);
    }
    const teamMap = new Map<string, Team>();
    sortedTeams.forEach((team: TeamDTO) => {
      teamMap.set(team.id, new Team(team));
    });
    localStorage.setItem('teamMap', serializeMap(teamMap));
    localStorage.setItem('teamId', String(currentTeamId));
    void Storage.setItem('teamId', String(currentTeamId));
    runInAction(() => {
      this.currentTeamId = currentTeamId;
      this.teamMap = teamMap;
    });
  }

  private hydrateUserMapFromTeamCache(): void {
    const teamDto = this.currentUser?.teamList?.find((t) => t.id === this.currentTeamId);
    const list = teamDto?.userList;
    if (!list?.length) {
      return;
    }
    list.forEach((member) => {
      this.userMap.set(String(member.id), member as unknown as User);
    });
  }

  async loadTeamData(): Promise<void> {
    if (!this.isLoggedIn || !this.currentTeamId) {
      return;
    }
    const members = await GlobalApi.getUserListByTeamId();
    if (typeof members === 'string') {
      runInAction(() => this.hydrateUserMapFromTeamCache());
    } else {
      runInAction(() => {
        members.forEach((member) => {
          this.userMap.set(String(member.id), member as unknown as User);
        });
      });
    }
    runInAction(() => {
      this.isLoaded = true;
    });
    await this.loadEnumConfigsByAll();
  }

  async loadEnumConfigsByAll(): Promise<void> {
    await Promise.all([1, 2, 3, 4, 5].map((t) => this.loadEnumConfigsByType(t)));
  }

  async loadEnumConfigsByType(type: number): Promise<void> {
    const list = await GlobalApi.getEnumConfigs(type);
    if (typeof list === 'string') {
      return;
    }
    runInAction(() => {
      switch (type) {
        case 1:
          this.enumConfigs.meetingOptions = list;
          break;
        case 2:
          this.enumConfigs.bizLineOptions = list;
          break;
        case 3:
          this.enumConfigs.userTypeOptions = list;
          break;
        case 4:
          this.enumConfigs.taskStatusOptions = list;
          break;
        case 5:
          this.enumConfigs.taskTypeOptions = list;
          break;
        default:
          break;
      }
    });
  }

  async login(payload: { phone: string; code: string }): Promise<{ token: string; userDTO: UserInfoDTO } | null> {
    const res = await LoginApi.login(payload.phone, payload.code);
    if (!res) {
      return null;
    }
    await Storage.setItem('token', res.token);
    localStorage.setItem('token', res.token);
    localStorage.setItem('userInfo', JSON.stringify(res.userDTO));
    runInAction(() => {
      this.currentUser = res.userDTO;
      this.isLoggedIn = true;
      this.enableLoad = true;
      this.profileSetupPrompt = needsProfileSetup(res.userDTO);
    });
    if (!res.userDTO.teamList || res.userDTO.teamList.length === 0) {
      window.location.hash = '#/join';
      return res;
    }
    this.syncCurrentTeamFromUser();
    await this.loadTeamData();
    return res;
  }

  async getVerificationCode(payload: { phone: string; captchaToken?: string }): Promise<boolean> {
    return LoginApi.sendVerificationCode(payload.phone, payload.captchaToken);
  }

  changeTeam(teamId: string): void {
    if (this.currentUser?.id) {
      recordTeamVisit(this.currentUser.id, teamId);
    }
    localStorage.setItem('teamId', String(teamId));
    void Storage.setItem('teamId', String(teamId));
    this.currentTeamId = teamId;
    window.location.reload();
  }

  getSortedTeamList(): TeamDTO[] {
    const list = this.currentUser?.teamList || [];
    if (!this.currentUser?.id) {
      return list;
    }
    return sortTeamListForUser(list, this.currentUser.id);
  }

  isCurrentTeamAdmin(): boolean {
    const userId = this.currentUser?.id;
    if (!userId || !this.currentTeamId) {
      return false;
    }
    const fromList = this.currentUser?.teamList?.find((t) => t.id === this.currentTeamId)?.teamAdminUser;
    const adminId = fromList ?? this.currentTeam?.teamAdminUser;
    if (adminId == null || adminId === '') {
      return false;
    }
    return String(adminId) === String(userId);
  }

  async updateUserInfo(payload: {
    id: string;
    userAvatar: string;
    realName: string;
    nickname: string;
    brief: string;
  }): Promise<boolean> {
    const ok = await ProfileApi.editUserInfo(payload);
    if (ok) {
      await this.getUserInfo(payload.id);
    }
    return ok;
  }

  async getUserInfo(userId: string): Promise<UserInfoDTO | null> {
    const info = await ProfileApi.queryUserInfo(userId);
    if (info) {
      localStorage.setItem('userInfo', JSON.stringify(info));
      runInAction(() => {
        this.currentUser = info;
        this.profileSetupPrompt = needsProfileSetup(info);
      });
    }
    return info;
  }

  dismissProfileSetupPrompt = (): void => {
    this.profileSetupPrompt = false;
  };
}

export const globalStore = new GlobalStore();
globalStore.loadFromLocalStorage();

/** @deprecated use globalStore.loadTeamData */
export const load = (): Promise<void> => globalStore.loadTeamData();
